import React from "react";
import { Upload } from "lucide-react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { formatImageUrl } from "@/lib/general";
import { ImageData } from "@/stores/tripBuilder/useCustomAccommodation";

interface ImageStepProps {
  images: ImageData[];
  uploadingImages: boolean;
  handleImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  removeImage: (index: number) => void;
  onNext: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
}

const ImageStep: React.FC<ImageStepProps> = ({
  images,
  uploadingImages,
  handleImageUpload,
  removeImage,
  onNext,
  onKeyDown,
}) => {
  return (
    <div className="w-full" onKeyDown={onKeyDown}>
      {images.length > 0 ? (
        <div className="grid grid-cols-4 gap-2 xl:gap-4 px-2">
          {images.map((image, index) => (
            <div key={index} className="relative group">
              <img
                src={formatImageUrl(
                  image.url || image.preview,
                  "medium",
                  false
                )}
                alt={`Preview ${index + 1}`}
                className="w-full h-24 object-cover rounded-lg"
              />
              <button
                onClick={() => removeImage(index)}
                className="z-20 absolute -top-2 -right-2 bg-white rounded-full ring-1 ring-neutral-300 w-5 h-5 flex items-center justify-center shadow-sm hover:bg-neutral-50 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <XMarkIcon className="w-3 h-3 text-neutral-500" />
              </button>
            </div>
          ))}
          {uploadingImages && (
            <div className="relative">
              <div className="w-full h-24 bg-neutral-100 rounded-lg flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
            </div>
          )}
          {images.length < 4 && !uploadingImages && (
            <div>
              <input
                type="file"
                accept="image/png, image/jpeg, image/webp"
                onChange={handleImageUpload}
                className="hidden"
                id="image-upload"
                multiple
              />
              <label
                htmlFor="image-upload"
                className="cursor-pointer h-24 border-2 border-dashed border-neutral-200 rounded-lg flex flex-col items-center justify-center gap-2 hover:bg-neutral-50 transition-colors"
              >
                <Upload className="w-5 h-5 text-neutral-500" />
                <p className="text-neutral-500 text-xs">Add more</p>
              </label>
            </div>
          )}
        </div>
      ) : (
        <div className="border-2 border-dashed border-neutral-200 rounded-xl p-6 text-center">
          <input
            type="file"
            accept="image/png, image/jpeg, image/webp"
            onChange={handleImageUpload}
            className="hidden"
            id="image-upload"
            multiple
            disabled={uploadingImages}
          />
          <label
            htmlFor="image-upload"
            className={`cursor-pointer flex flex-col items-center gap-3 ${
              uploadingImages ? "opacity-50" : ""
            }`}
          >
            <div className="w-14 h-14 bg-neutral-100 rounded-full flex items-center justify-center">
              {uploadingImages ? (
                <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <Upload className="w-6 h-6 text-neutral-500" />
              )}
            </div>
            <div>
              <p className="text-neutral-600 text-sm">
                {uploadingImages
                  ? "Uploading..."
                  : "Click to upload or drag and drop"}
              </p>
              <p className="text-neutral-400 text-xs mt-1">
                PNG, JPG or WEBP (max 4 images)
              </p>
            </div>
          </label>
        </div>
      )}
    </div>
  );
};

export default ImageStep;
